import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Clock, CheckCircle, ArrowLeft, AlertCircle, Loader2 } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button, Card, Input } from '../../components/ui';
import { configurationsAPI } from '../../services/api';
import { useBookings } from '../../hooks/booking/useBookingContextSlices';
import OnboardingRibbon from '../../features/home/OnboardingRibbon';

/**
 * §8/§9 rule-based slot setup. Replaces the old Date Range → Price Setup wizard
 * with a single recurring rule: which days, open/close hours, slot duration,
 * optional break, and price. The server stores the rule and regenerates the
 * actual slots over a rolling horizon — no expiring date window to maintain.
 */

const STORAGE_KEY = 'groundaale_slot_rule_service_v1';

const DAYS = [
  { value: 1, short: 'Mon' },
  { value: 2, short: 'Tue' },
  { value: 3, short: 'Wed' },
  { value: 4, short: 'Thu' },
  { value: 5, short: 'Fri' },
  { value: 6, short: 'Sat' },
  { value: 0, short: 'Sun' },
];

const DURATION_PRESETS = [
  { label: '1 hr', minutes: 60 },
  { label: '1.5 hr', minutes: 90 },
  { label: '2 hr', minutes: 120 },
  { label: '3 hr', minutes: 180 },
  { label: '4 hr', minutes: 240 },
  { label: '5 hr', minutes: 300 },
  { label: '6 hr', minutes: 360 },
  { label: '8 hr', minutes: 480 },
];

const DAY_SCOPES = [
  { value: 'all', label: 'All days', days: [] },
  { value: 'weekdays', label: 'Weekdays', days: [1, 2, 3, 4, 5] },
  { value: 'saturday', label: 'Saturday', days: [6] },
  { value: 'sunday', label: 'Sunday', days: [0] },
];

// TIME_BANDS kept only for reading back legacy rules saved before slot-position model
const LEGACY_TIME_BANDS = [
  { value: 'all', label: 'All day', from: null, to: null },
  { value: 'morning', label: 'Morning', from: '00:00', to: '12:00' },
  { value: 'afternoon', label: 'Afternoon', from: '12:00', to: '17:00' },
  { value: 'evening', label: 'Evening', from: '17:00', to: '23:59' },
];

const sameDays = (a = [], b = []) =>
  a.length === b.length && [...a].sort().join() === [...b].sort().join();

// Map a UI variation to the backend price_rule shape.
const variationToRule = (v) => {
  const scope = DAY_SCOPES.find((s) => s.value === v.dayScope) || DAY_SCOPES[0];
  return {
    days_of_week: scope.days,
    slot_position: v.slotPosition || null, // 1-based
    time_from: null,
    time_to: null,
    price: parseFloat(v.price) || 0,
    price_unit: 'per_slot',
  };
};

// Map a backend price_rule back to a UI variation.
const ruleToVariation = (r) => {
  const scope = DAY_SCOPES.find((s) => sameDays(s.days, r.days_of_week || [])) || DAY_SCOPES[0];
  // Prefer slot_position; fall back to legacy time-band label for old data
  const slotPosition = r.slot_position != null ? String(r.slot_position) : '';
  const legacyBand = r.time_from
    ? (LEGACY_TIME_BANDS.find((t) => t.from === String(r.time_from).slice(0, 5))?.value || '')
    : '';
  return {
    dayScope: scope.value,
    slotPosition,
    legacyBand, // only used for display of old records
    price: r.price != null ? String(Number(r.price)) : '',
  };
};

const readStoredService = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const ConfigureSlotRule = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { reloadBookings } = useBookings();
  const [searchParams] = useSearchParams();
  const inOnboarding = searchParams.get('onboarding') === '1';

  const service = useMemo(() => {
    const fromNav = location.state?.serviceData || null;
    if (fromNav) {
      try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromNav)); } catch { /* ignore */ }
      return fromNav;
    }
    return readStoredService();
  }, [location.state]);

  const [days, setDays] = useState([]); // empty = every day
  const [openTime, setOpenTime] = useState('06:00');
  const [closeTime, setCloseTime] = useState('22:00');
  const [slotMinutes, setSlotMinutes] = useState(() => {
    // Derive a sport-appropriate default for first-time setup.
    // Cricket full-ground = 240, Football full-pitch = 120, everything else = 60.
    const slug = (location.state?.serviceData?.sport_slug || '').toLowerCase();
    if (slug === 'cricket') return 240;
    if (slug === 'football') return 120;
    return 60;
  });
  const [breakMinutes, setBreakMinutes] = useState(0);
  const [maxSlots, setMaxSlots] = useState(''); // '' = no cap (auto from hours)
  const [price, setPrice] = useState('');
  const [priceUnit, setPriceUnit] = useState('per_slot');
  // Differential pricing: each variation = { dayScope, timeBand, price }.
  // dayScope: 'all' | 'weekdays' | 'saturday' | 'sunday'; timeBand: 'all' | 'morning' | 'afternoon' | 'evening'.
  const [variations, setVariations] = useState([]);
  // Holiday pricing: specific dates with their own flat price.
  const [holidays, setHolidays] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedSummary, setSavedSummary] = useState(null);

  // Redirect out if we have no service to configure.
  useEffect(() => {
    if (!service) navigate('/dashboard/configure-service', { replace: true });
  }, [service, navigate]);

  // Prefill from an existing rule so editing is non-destructive.
  useEffect(() => {
    let active = true;
    (async () => {
      if (!service?.id) { setLoading(false); return; }
      try {
        const res = await configurationsAPI.getSlotRule(service.id);
        if (active && res?.success && res.data) {
          const r = res.data;
          setDays(Array.isArray(r.days_of_week) ? r.days_of_week : []);
          setOpenTime(String(r.open_time).slice(0, 5));
          setCloseTime(String(r.close_time).slice(0, 5));
          setSlotMinutes(Number(r.slot_minutes) || 60);
          setBreakMinutes(Number(r.break_minutes) || 0);
          setPrice(r.price != null ? String(Number(r.price)) : '');
          setPriceUnit(r.price_unit || 'per_slot');
          if (r.max_slots != null) setMaxSlots(String(r.max_slots));
          if (Array.isArray(r.price_rules)) {
            setVariations(r.price_rules.map(ruleToVariation));
          }
          if (Array.isArray(r.holidays)) {
            setHolidays(r.holidays.map((h) => ({
              date: String(h.holiday_date).slice(0, 10),
              price: h.price != null ? String(Number(h.price)) : '',
              label: h.label || '',
            })));
          }
        }
      } catch {
        // first-time setup — fall back to defaults
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [service?.id]);

  const toggleDay = (value) => {
    setDays((prev) =>
      prev.includes(value) ? prev.filter((d) => d !== value) : [...prev, value]
    );
  };

  // Live preview of generated slots so the owner sees the result before saving.
  const previewSlots = useMemo(() => {
    const toMin = (t) => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    const open = toMin(openTime);
    const close = toMin(closeTime);
    const step = slotMinutes + breakMinutes;
    if (!(close > open) || slotMinutes <= 0) return [];
    const cap = parseInt(maxSlots, 10) > 0 ? parseInt(maxSlots, 10) : 50;
    const out = [];
    for (let c = open; c + slotMinutes <= close && out.length < cap; c += step) {
      const fmt = (mins) => `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
      out.push(`${fmt(c)}–${fmt(c + slotMinutes)}`);
    }
    return out;
  }, [openTime, closeTime, slotMinutes, breakMinutes, maxSlots]);

  const handleSave = async () => {
    setError('');
    if (closeTime <= openTime) { setError('Closing time must be after opening time.'); return; }
    if (previewSlots.length === 0) { setError('These timings produce no slots — shorten the slot length or widen the hours.'); return; }
    const numericPrice = parseFloat(price);
    if (isNaN(numericPrice) || numericPrice < 0) { setError('Enter a valid price.'); return; }

    setSaving(true);
    try {
      const res = await configurationsAPI.saveSlotRule({
        service_id: service.id,
        days_of_week: days, // empty = every day
        open_time: openTime,
        close_time: closeTime,
        slot_minutes: slotMinutes,
        break_minutes: breakMinutes,
        max_slots: parseInt(maxSlots, 10) > 0 ? parseInt(maxSlots, 10) : null,
        price: numericPrice,
        price_unit: priceUnit,
        price_rules: variations
          .filter((v) => parseFloat(v.price) > 0)
          .map(variationToRule),
        holidays: holidays
          .filter((h) => h.date && parseFloat(h.price) >= 0)
          .map((h) => ({ date: h.date, price: parseFloat(h.price) || 0, label: h.label || null })),
      });
      if (res?.success) {
        // Refresh the booking context so the calendar shows the newly generated
        // slots immediately instead of the stale set loaded at app start.
        try { await reloadBookings(); } catch { /* non-fatal */ }
        setSavedSummary(res.data);
      } else {
        setError(res?.message || 'Could not save. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!service) return null;

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        {inOnboarding && <OnboardingRibbon />}
        <button
          type="button"
          onClick={() => navigate('/dashboard/configure-service')}
          className="inline-flex items-center gap-1.5 text-sm text-ga-text-muted hover:text-ga-text-primary transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to services
        </button>

        <div>
          <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
            <Clock className="h-6 w-6 text-ga-primary" />
            Timings & slots
          </h1>
          <p className="text-ga-text-muted mt-1">
            Set up <span className="font-medium text-ga-text-primary">{service.name}</span> once —
            slots are generated automatically and never expire.
          </p>
        </div>

        {savedSummary ? (
          <Card className="p-6 text-center space-y-3">
            <CheckCircle className="h-12 w-12 text-ga-primary mx-auto" />
            <h2 className="text-lg font-semibold text-ga-text-primary">Slots are live</h2>
            <p className="text-ga-text-muted text-sm">
              Generated {savedSummary.slotCount} slot{savedSummary.slotCount === 1 ? '' : 's'} per day
              through {savedSummary.generatedThrough}. Edit this rule any time — slots update instantly.
            </p>
            <div className="flex items-center justify-center gap-3 pt-2">
              <Button variant="secondary" onClick={() => setSavedSummary(null)}>Edit rule</Button>
              {inOnboarding ? (
                <Button onClick={() => navigate('/dashboard/book-now?onboarding=1&step=first_booking')}>
                  Next: record your first booking
                </Button>
              ) : (
                <Button onClick={() => navigate('/dashboard/book-now')}>Go to calendar</Button>
              )}
            </div>
          </Card>
        ) : (
          <Card className="p-6 space-y-6">
            {error && (
              <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Days */}
            <div>
              <label className="block text-sm font-medium text-ga-text-primary mb-2">Open on</label>
              <div className="flex flex-wrap gap-2">
                {DAYS.map((d) => {
                  const active = days.length === 0 || days.includes(d.value);
                  return (
                    <button
                      key={d.value}
                      type="button"
                      onClick={() => toggleDay(d.value)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                        days.includes(d.value)
                          ? 'bg-ga-primary text-white border-ga-primary'
                          : 'bg-transparent text-ga-text-muted border-ga-border hover:border-ga-primary/50'
                      }`}
                    >
                      {d.short}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1.5 text-xs text-ga-text-muted">
                {days.length === 0 ? 'Open every day (tap to limit to specific days).' : `Open ${days.length} day(s) a week.`}
              </p>
            </div>

            {/* Hours */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ga-text-primary mb-2">Opens</label>
                <Input type="time" value={openTime} onChange={(e) => setOpenTime(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-ga-text-primary mb-2">Closes</label>
                <Input type="time" value={closeTime} onChange={(e) => setCloseTime(e.target.value)} />
              </div>
            </div>

            {/* Duration chips */}
            <div>
              <label className="block text-sm font-medium text-ga-text-primary mb-2">Slot length</label>
              <div className="flex flex-wrap gap-2">
                {DURATION_PRESETS.map((p) => (
                  <button
                    key={p.minutes}
                    type="button"
                    onClick={() => setSlotMinutes(p.minutes)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      slotMinutes === p.minutes
                        ? 'bg-ga-primary text-white border-ga-primary'
                        : 'bg-transparent text-ga-text-muted border-ga-border hover:border-ga-primary/50'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Break */}
            <div>
              <label className="block text-sm font-medium text-ga-text-primary mb-2">
                Break between slots (minutes)
              </label>
              <Input
                type="number"
                min="0"
                step="5"
                value={breakMinutes}
                onChange={(e) => setBreakMinutes(Math.max(0, parseInt(e.target.value, 10) || 0))}
                className="max-w-[140px]"
              />
            </div>

            {/* Max slots cap */}
            <div>
              <label className="block text-sm font-medium text-ga-text-primary mb-2">
                Number of slots per day <span className="text-ga-text-muted font-normal">(optional cap)</span>
              </label>
              <Input
                type="number"
                min="1"
                step="1"
                value={maxSlots}
                onChange={(e) => {
                  const v = e.target.value;
                  setMaxSlots(v === '' ? '' : String(Math.max(1, parseInt(v, 10) || 1)));
                }}
                placeholder={`Auto (${previewSlots.length} from hours)`}
                className="max-w-[200px]"
              />
              <p className="mt-1 text-xs text-ga-text-muted">
                Leave blank to fill all available time. Set a number to cap at exactly that many slots per day.
              </p>
            </div>

            {/* Price */}
            <div>
              <label className="block text-sm font-medium text-ga-text-primary mb-2">Price</label>
              <div className="flex items-center gap-2">
                <span className="text-ga-text-muted">₹</span>
                <Input
                  type="number"
                  min="0"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0"
                  className="max-w-[160px]"
                />
                <select
                  value={priceUnit}
                  onChange={(e) => setPriceUnit(e.target.value)}
                  className="rounded-lg border border-ga-border bg-transparent px-3 py-2 text-sm text-ga-text-primary"
                >
                  <option value="per_slot">per slot</option>
                  <option value="per_hour">per hour</option>
                </select>
              </div>
            </div>

            {/* Price variations — per slot position + day scope */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-ga-text-primary">
                  Price variations <span className="text-ga-text-muted font-normal">(optional)</span>
                </label>
                <button
                  type="button"
                  onClick={() => setVariations((prev) => [...prev, { dayScope: 'saturday', slotPosition: '1', price: '' }])}
                  className="text-sm text-ga-primary hover:underline"
                >
                  + Add variation
                </button>
              </div>
              <p className="text-xs text-ga-text-muted mb-3">
                Set a different price for a specific slot (e.g. Slot 1 on Saturdays). Base price applies to everything else.
              </p>
              {variations.length === 0 ? (
                <p className="text-sm text-ga-text-muted">No variations — every slot uses the base price.</p>
              ) : (
                <div className="space-y-2">
                  {variations.map((v, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2 rounded-lg border border-ga-border p-2">
                      <select
                        value={v.dayScope}
                        onChange={(e) => setVariations((prev) => prev.map((x, j) => j === i ? { ...x, dayScope: e.target.value } : x))}
                        className="rounded-lg border border-ga-border bg-transparent px-2 py-1.5 text-sm text-ga-text-primary"
                      >
                        {DAY_SCOPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                      <select
                        value={v.slotPosition || ''}
                        onChange={(e) => setVariations((prev) => prev.map((x, j) => j === i ? { ...x, slotPosition: e.target.value } : x))}
                        className="rounded-lg border border-ga-border bg-transparent px-2 py-1.5 text-sm text-ga-text-primary"
                      >
                        <option value="">All slots</option>
                        {previewSlots.map((label, idx) => (
                          <option key={idx + 1} value={String(idx + 1)}>Slot {idx + 1} ({label})</option>
                        ))}
                      </select>
                      <span className="text-ga-text-muted text-sm">₹</span>
                      <Input
                        type="number"
                        min="0"
                        value={v.price}
                        onChange={(e) => setVariations((prev) => prev.map((x, j) => j === i ? { ...x, price: e.target.value } : x))}
                        placeholder="price"
                        className="max-w-[110px]"
                      />
                      <button
                        type="button"
                        onClick={() => setVariations((prev) => prev.filter((_, j) => j !== i))}
                        className="ml-auto text-xs text-red-500 hover:text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Holiday pricing (specific dates) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-ga-text-primary">
                  Holiday pricing <span className="text-ga-text-muted font-normal">(optional)</span>
                </label>
                <button
                  type="button"
                  onClick={() => setHolidays((prev) => [...prev, { date: '', price: '', label: '' }])}
                  className="text-sm text-ga-primary hover:underline"
                >
                  + Add holiday
                </button>
              </div>
              <p className="text-xs text-ga-text-muted mb-3">
                Set a special price for specific dates (e.g. festivals). A holiday price overrides all other pricing that day.
              </p>
              {holidays.length === 0 ? (
                <p className="text-sm text-ga-text-muted">No holiday prices set.</p>
              ) : (
                <div className="space-y-2">
                  {holidays.map((h, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2 rounded-lg border border-ga-border p-2">
                      <Input
                        type="date"
                        value={h.date}
                        onChange={(e) => setHolidays((prev) => prev.map((x, j) => j === i ? { ...x, date: e.target.value } : x))}
                        className="max-w-[160px]"
                      />
                      <Input
                        type="text"
                        value={h.label}
                        onChange={(e) => setHolidays((prev) => prev.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                        placeholder="Label (e.g. Diwali)"
                        className="max-w-[150px]"
                      />
                      <span className="text-ga-text-muted text-sm">₹</span>
                      <Input
                        type="number"
                        min="0"
                        value={h.price}
                        onChange={(e) => setHolidays((prev) => prev.map((x, j) => j === i ? { ...x, price: e.target.value } : x))}
                        placeholder="price"
                        className="max-w-[110px]"
                      />
                      <button
                        type="button"
                        onClick={() => setHolidays((prev) => prev.filter((_, j) => j !== i))}
                        className="ml-auto text-xs text-red-500 hover:text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Preview */}
            <div className="rounded-lg bg-ga-primary/5 border border-ga-primary/15 p-4">
              <p className="text-sm font-medium text-ga-text-primary mb-2">
                {previewSlots.length} slot{previewSlots.length === 1 ? '' : 's'} per open day
              </p>
              <div className="flex flex-wrap gap-1.5">
                {previewSlots.slice(0, 12).map((s) => (
                  <span key={s} className="text-xs px-2 py-1 rounded bg-white/70 border border-ga-primary/20 text-ga-text-muted">
                    {s}
                  </span>
                ))}
                {previewSlots.length > 12 && (
                  <span className="text-xs px-2 py-1 text-ga-text-muted">+{previewSlots.length - 12} more</span>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={saving || loading}>
                {saving ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
                ) : (
                  'Save & generate slots'
                )}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ConfigureSlotRule;
